/**
 * Upsell Products Integration
 * 
 * This script handles the integration of upsell products with the theme's
 * add to cart and dynamic checkout functionality.
 * 
 * Key features:
 * - Intercepts form submission to add main product + upsells together
 * - Handles both "Add to Cart" and "Buy Now" buttons
 * - Ensures proper cart state before redirecting
 * - Does not interfere with default theme AddToCartForm
 */

document.addEventListener('DOMContentLoaded', function() {
  // Initialize upsell products functionality
  initUpsellProducts();
});

function initUpsellProducts() {
  const upsellContainer = document.querySelector('.upsell-products');
  if (!upsellContainer) return;

  const checkboxes = upsellContainer.querySelectorAll('[data-upsell-checkbox]');
  const variantSelects = upsellContainer.querySelectorAll('[data-upsell-variant-select]');
  const titleLinks = upsellContainer.querySelectorAll('[data-upsell-title-link]');
  const totalPriceContainer = document.querySelector('[data-upsell-total-container]');
  const totalPriceElement = document.querySelector('[data-upsell-total-price]');
  
  // Check if we should hide buy now buttons
  const hideBuyNowButtons = upsellContainer.getAttribute('data-hide-buy-now-buttons') === 'true';
  
  // Track if we're currently processing a submission to prevent double-submission
  let isProcessingSubmission = false;

  // Handle checkbox changes
  checkboxes.forEach(function(checkbox) {
    checkbox.addEventListener('change', function() {
      updateUpsellData();
      updateTotalPrice();
      if (hideBuyNowButtons) {
        updateBuyNowButtons();
      }
    });
  });

  // Prevent title links from triggering checkbox selection
  // Allow the link to navigate naturally without interfering with checkbox
  titleLinks.forEach(function(link) {
    link.addEventListener('click', function(e) {
      // Stop the click from propagating to the label which would toggle the checkbox
      e.stopPropagation();
      // Allow the link to navigate normally (don't preventDefault)
    });
  });

  // Handle variant selection changes
  variantSelects.forEach(function(select) {
    // console.log('Upsell duped: Initializing select element', {
    //   productId: select.getAttribute('data-product-id'),
    //   selectedIndex: select.selectedIndex,
    //   selectedValue: select.value,
    //   selectedOption: select.options[select.selectedIndex]
    // });
    
    select.addEventListener('change', function() {
      // console.log('Upsell duped: Variant changed by user');
      updateVariantPrice(select);
      updateUpsellData();
      updateTotalPrice();
    });
    
    // Update price on page load to ensure correct initial display
    // console.log('Upsell duped: Calling updateVariantPrice on page load');
    updateVariantPrice(select);
    
    // Resize select to fit longest option on page load (only once)
    resizeSelectToFitLongestOption(select);
  });
  
  // Initialize buy now button state on page load
  if (hideBuyNowButtons) {
    updateBuyNowButtons();
  }
  
  /**
   * Resize select element to fit the longest option text
   * Called once on page load to set appropriate width
   * @param {HTMLSelectElement} select - The select element to resize
   */
  function resizeSelectToFitLongestOption(select) {
    // Create a temporary span to measure text width
    var tempSpan = document.createElement('span');
    tempSpan.style.visibility = 'hidden';
    tempSpan.style.position = 'absolute';
    tempSpan.style.whiteSpace = 'nowrap';
    tempSpan.style.fontSize = window.getComputedStyle(select).fontSize;
    tempSpan.style.fontFamily = window.getComputedStyle(select).fontFamily;
    tempSpan.style.fontWeight = window.getComputedStyle(select).fontWeight;
    
    document.body.appendChild(tempSpan);
    
    // Find the longest option text
    var maxTextWidth = 0;
    for (var i = 0; i < select.options.length; i++) {
      tempSpan.textContent = select.options[i].text;
      var textWidth = tempSpan.offsetWidth;
      if (textWidth > maxTextWidth) {
        maxTextWidth = textWidth;
      }
    }
    
    document.body.removeChild(tempSpan);
    
    // Add padding for dropdown arrow and borders (approximately 35px)
    var selectWidth = maxTextWidth + 35;
    
    // Apply min and max constraints
    var minWidth = 80;
    var maxWidth = 200;
    selectWidth = Math.max(minWidth, Math.min(maxWidth, selectWidth));
    
    select.style.width = selectWidth + 'px';
  }
  
  /**
   * Updates the price display for a specific variant selection
   * @param {HTMLSelectElement} select - The variant select element
   */
  function updateVariantPrice(select) {
    const productId = select.getAttribute('data-product-id');
    const selectedOption = select.options[select.selectedIndex];
    const variantId = selectedOption.value;
    const price = selectedOption.getAttribute('data-price');
    const comparePrice = selectedOption.getAttribute('data-compare-price');
    
    // console.log('Upsell duped: Updating variant price', {
    //   productId: productId,
    //   variantId: variantId,
    //   price: price,
    //   comparePrice: comparePrice
    // });
    
    // Update the corresponding checkbox value
    const checkbox = upsellContainer.querySelector(`[data-upsell-checkbox][data-product-id="${productId}"]`);
    if (checkbox) {
      checkbox.value = variantId;
      checkbox.setAttribute('data-variant-id', variantId);
    }
    
    // Update regular price display
    const priceElement = upsellContainer.querySelector(`[data-upsell-price="${productId}"]`);
    // console.log('Upsell duped: Price element found:', priceElement);
    
    if (priceElement && price) {
      if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney) {
        const formattedPrice = theme.Currency.formatMoney(parseInt(price), theme.settings.moneyFormat);
        // console.log('Upsell duped: Setting formatted price:', formattedPrice);
        priceElement.textContent = formattedPrice;
      } else {
        // Fallback formatting
        const fallbackPrice = '$' + (parseInt(price) / 100).toFixed(2);
        // console.log('Upsell duped: Setting fallback price:', fallbackPrice);
        priceElement.textContent = fallbackPrice;
      }
      // console.log('Upsell duped: Price element text after update:', priceElement.textContent);
    } else {
      // console.log('Upsell duped: Price element or price not found', {
      //   hasElement: !!priceElement,
      //   hasPrice: !!price
      // });
    }
    
    // Update compare-at price display (if element exists)
    const comparePriceElement = upsellContainer.querySelector(`[data-upsell-compare-price="${productId}"]`);
    if (comparePriceElement) {
      // Only show compare price if it exists and is greater than regular price
      if (comparePrice && parseInt(comparePrice) > parseInt(price)) {
        if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney) {
          comparePriceElement.textContent = theme.Currency.formatMoney(parseInt(comparePrice), theme.settings.moneyFormat);
        } else {
          comparePriceElement.textContent = '$' + (parseInt(comparePrice) / 100).toFixed(2);
        }
        comparePriceElement.style.display = '';
      } else {
        // Hide compare price if not applicable
        comparePriceElement.style.display = 'none';
      }
    }
    
    // Update the product title link to point to the selected variant
    const titleLink = upsellContainer.querySelector(`[data-upsell-title-link][data-product-id="${productId}"]`);
    if (titleLink) {
      const currentHref = titleLink.getAttribute('href');
      const baseUrl = currentHref.split('?')[0]; // Get URL without query params
      titleLink.setAttribute('href', baseUrl + '?variant=' + variantId);
    }
  }

  /**
   * Updates the global upsell data store
   */
  function updateUpsellData() {
    const checkedBoxes = upsellContainer.querySelectorAll('[data-upsell-checkbox]:checked');
    const upsellItems = Array.from(checkedBoxes).map(function(checkbox) {
      return {
        id: checkbox.value,
        quantity: 1
      };
    });

    // Store upsell data in a global variable for form submission handler
    window.upsellItems = upsellItems;
  }

  /**
   * Gets the current selected upsell items
   */
  function getSelectedUpsells() {
    const checkedBoxes = upsellContainer.querySelectorAll('[data-upsell-checkbox]:checked');
    return Array.from(checkedBoxes).map(function(checkbox) {
      return {
        id: checkbox.value,
        quantity: 1
      };
    });
  }

  /**
   * Hide or show buy now buttons based on upsell selection
   */
  function updateBuyNowButtons() {
    const selectedUpsells = getSelectedUpsells();
    const hasSelectedUpsells = selectedUpsells.length > 0;
    
    // Find all buy now buttons (accelerated checkout buttons)
    const buyNowButtons = document.querySelectorAll(
      '[data-shopify-payment-button], ' +
      '.shopify-payment-button, ' +
      '[data-dynamic-checkout-button], ' +
      '.dynamic-checkout__content, ' +
      '.shopify-payment-button__button, ' +
      'button[data-shopify="shopify_pay"]'
    );
    
    buyNowButtons.forEach(function(button) {
      if (hasSelectedUpsells) {
        // Hide buy now buttons when upsells are selected
        button.style.display = 'none';
        button.setAttribute('data-upsell-hidden', 'true');
        // console.log('Upsell duped: Hiding buy now button');
      } else {
        // Show buy now buttons when no upsells are selected
        if (button.getAttribute('data-upsell-hidden') === 'true') {
          button.style.display = '';
          button.removeAttribute('data-upsell-hidden');
          // console.log('Upsell duped: Showing buy now button');
        }
      }
    });
  }

  /**
   * Calculate and update the total price (main product + selected upsells)
   */
  function updateTotalPrice() {
    if (!totalPriceContainer || !totalPriceElement) return;

    const selectedUpsells = getSelectedUpsells();
    
    // If no upsells selected, hide total price
    if (selectedUpsells.length === 0) {
      totalPriceContainer.style.display = 'none';
      return;
    }

    // Get main product price from the main product form
    const productFormElement = document.querySelector('form[action*="/cart/add"]');
    if (!productFormElement) {
      // console.log('Upsell duped: No product form found');
      return;
    }
    
    const mainVariantInput = productFormElement.querySelector('[name="id"]');
    if (!mainVariantInput) {
      // console.log('Upsell duped: No main variant input found');
      return;
    }
    
    const mainVariantId = mainVariantInput.value;
    // console.log('Upsell duped: Main variant ID:', mainVariantId);
    
    let mainProductPrice = 0;
    
    // Get price from the displayed product price element
    const priceElement = document.querySelector('[data-product-price]');
    if (priceElement) {
      const priceText = priceElement.textContent.trim();
      // console.log('Upsell duped: Main product price text:', priceText);
      
      // Extract numeric value from price text (e.g., "$1,200.00" -> 120000)
      const priceMatch = priceText.match(/[\d,]+\.?\d*/);
      if (priceMatch) {
        const numericPrice = parseFloat(priceMatch[0].replace(/,/g, ''));
        mainProductPrice = Math.round(numericPrice * 100); // Convert to cents
        // console.log('Upsell duped: Main product price (cents):', mainProductPrice);
      }
    } else {
      // console.log('Upsell duped: No price element found');
    }
    
    // console.log('Upsell duped: Final main product price:', mainProductPrice);

    // Calculate total of selected upsells
    let upsellsTotal = 0;
    // console.log('Upsell duped: Processing', selectedUpsells.length, 'upsells');
    
    selectedUpsells.forEach(function(upsell) {
      // console.log('Upsell duped: Processing upsell variant ID:', upsell.id);
      
      let price = 0;
      
      // First, try to find the price from the option tag (for products with variants)
      const variantOption = upsellContainer.querySelector('option[value="' + upsell.id + '"]');
      if (variantOption) {
        const priceAttr = variantOption.getAttribute('data-price');
        // console.log('Upsell duped: Found price from option:', priceAttr);
        price = parseInt(priceAttr) || 0;
      } else {
        // If no option found, this product doesn't have a variant selector
        // So get the price from the checkbox (for products without variants)
        const checkbox = upsellContainer.querySelector('[data-upsell-checkbox][value="' + upsell.id + '"]');
        if (checkbox && checkbox.hasAttribute('data-price')) {
          const priceAttr = checkbox.getAttribute('data-price');
          // console.log('Upsell duped: Found price from checkbox:', priceAttr);
          price = parseInt(priceAttr) || 0;
        } else {
          // console.log('Upsell duped: No price found for variant ID:', upsell.id);
        }
      }
      
      upsellsTotal += price * upsell.quantity;
      // console.log('Upsell duped: Added price:', price, 'Running total:', upsellsTotal);
    });

    // console.log('Upsell duped: Upsells total:', upsellsTotal);

    // Calculate grand total
    const grandTotal = mainProductPrice + upsellsTotal;
    // console.log('Upsell duped: Grand total:', grandTotal);

    // Format and display total
    if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney) {
      totalPriceElement.textContent = theme.Currency.formatMoney(grandTotal, theme.settings.moneyFormat);
    } else {
      // Fallback formatting
      totalPriceElement.textContent = '$' + (grandTotal / 100).toFixed(2);
    }

    // Show total price container
    totalPriceContainer.style.display = 'flex';
  }

  /**
   * Clears all selected upsell checkboxes
   */
  function clearUpsellSelections() {
    const checkedBoxes = upsellContainer.querySelectorAll('[data-upsell-checkbox]:checked');
    checkedBoxes.forEach(function(checkbox) {
      checkbox.checked = false;
    });
  }

  /**
   * Main form submission handler
   * Intercepts the form submission BEFORE the theme's handler to add upsells
   */
  const productForm = document.querySelector('form[action*="/cart/add"]');
  if (productForm) {
    // Capture the form submit event in the capture phase (before theme's handler)
    productForm.addEventListener('submit', function(e) {
      const selectedUpsells = getSelectedUpsells();
      
      // Only intercept if there are upsell products selected
      if (selectedUpsells.length > 0 && !isProcessingSubmission) {
        // console.log('Intercepting form submission with upsells:', selectedUpsells);
        
        // Prevent default form submission
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        isProcessingSubmission = true;
        
        // Determine if this is a "buy now" submission
        const submitter = e.submitter;
        const isBuyNow = submitter && (
          submitter.classList.contains('shopify-payment-button__button') ||
          submitter.closest('.shopify-payment-button') !== null
        );
        
        // Add all products together
        addAllProductsToCart(productForm, selectedUpsells, isBuyNow)
          .finally(function() {
            isProcessingSubmission = false;
          });
      }
    }, true); // Use capture phase to run before theme's handler
  }

  /**
   * Handles dynamic checkout button clicks
   * Dynamic checkout buttons don't always trigger form submit events
   */
  function setupDynamicCheckoutInterceptor() {
    // Wait for Shopify's payment buttons to load
    const checkForPaymentButtons = setInterval(function() {
      const paymentButtons = document.querySelectorAll('.shopify-payment-button__button');
      
      if (paymentButtons.length > 0) {
        clearInterval(checkForPaymentButtons);
        
        paymentButtons.forEach(function(button) {
          button.addEventListener('click', function(e) {
            const selectedUpsells = getSelectedUpsells();
            
            if (selectedUpsells.length > 0 && !isProcessingSubmission) {
              // console.log('Intercepting payment button click with upsells:', selectedUpsells);
              
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              
              isProcessingSubmission = true;
              
              const form = button.closest('form');
              if (form) {
                addAllProductsToCart(form, selectedUpsells, true)
                  .finally(function() {
                    isProcessingSubmission = false;
                  });
              }
            }
          }, true); // Use capture phase
        });
      }
    }, 100);
    
    // Stop checking after 5 seconds
    setTimeout(function() {
      clearInterval(checkForPaymentButtons);
    }, 5000);
  }
  
  // Initialize dynamic checkout interceptor
  setupDynamicCheckoutInterceptor();

  /**
   * Adds main product and upsells to cart together
   * @param {HTMLFormElement} form - The product form
   * @param {Array} upsellItems - Array of upsell items to add
   * @param {Boolean} redirectToCheckout - Whether to redirect to checkout or cart
   * @returns {Promise} - Promise that resolves when items are added
   */
  function addAllProductsToCart(form, upsellItems, redirectToCheckout) {
    return new Promise(function(resolve, reject) {
      const formData = new FormData(form);
      const mainProductId = formData.get('id');
      const mainProductQuantity = parseInt(formData.get('quantity') || 1);
      
      if (!mainProductId) {
        console.error('No product variant selected');
        reject(new Error('No product variant selected'));
        return;
      }
      
      // console.log('Adding to cart:', {
      //   mainProduct: mainProductId,
      //   quantity: mainProductQuantity,
      //   upsells: upsellItems
      // });

      // Prepare all items (main product + selected upsells)
      const allItems = [
        {
          id: mainProductId,
          quantity: mainProductQuantity
        },
        ...upsellItems
      ];

      // Build the request body for adding multiple items
      const cartFormData = new FormData();
      allItems.forEach(function(item, index) {
        cartFormData.append('items[' + index + '][id]', item.id);
        cartFormData.append('items[' + index + '][quantity]', item.quantity);
      });

      // Add all items to cart
      fetch('/cart/add.js', {
        method: 'POST',
        body: cartFormData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      .then(function(response) {
        if (!response.ok) {
          return response.json().then(function(err) {
            throw new Error(err.description || 'Failed to add items to cart');
          });
        }
        return response.json();
      })
      .then(function(data) {
        // console.log('Successfully added items to cart:', data);
        
        // Clear upsell selections
        clearUpsellSelections();
        
        // Hide total price
        updateTotalPrice();
        
        // Redirect to appropriate page
        if (redirectToCheckout) {
          // Redirect to checkout
          window.location.href = '/checkout';
        } else {
          // Check theme settings to determine redirect behavior
          if (window.theme && window.theme.settings && window.theme.settings.cartType === 'page') {
            // Redirect to cart page
            window.location.href = '/cart';
          } else {
            // Cart drawer - dispatch event to open it
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            
            // Optionally trigger the theme's success handler
            if (window.theme && window.theme.cart) {
              document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
                detail: { product: data },
                bubbles: true
              }));
            }
          }
        }
        
        resolve(data);
      })
      .catch(function(error) {
        // console.error('Error adding products to cart:', error);
        
        // Display error to user
        // alert('There was an error adding items to your cart. Please try again.');
        
        // Reset submission flag
        isProcessingSubmission = false;
        
        reject(error);
      });
    });
  }

  // Listen for main product variant changes to update total price
  document.addEventListener('variant:change', function(event) {
    // console.log('Upsell duped: Main product variant changed');
    updateTotalPrice();
  });
}
